import { SelectChangeEvent, useAutocomplete, Box, IconButton, alpha, TextField as MuiTextField } from "@mui/material"
import ClearIcon from '@mui/icons-material/Clear';
import { FormikContextType, useFormikContext } from "formik"
import { Identifier, RecordMap, ListControllerProps, useUnselectAll, useListContext } from "ra-core"
import { useRef, useCallback, useEffect, useMemo, useState, MutableRefObject } from "react"
import { decideDataTypeMaskingKind, integerRangeCheck, nonIntegerRangeCheck } from "~/components/governance/fieldEdit/maskingRuleDataTypesMap"
import { SimpleOption } from "~/components/governance/search/AmbientFacilitiesContext"
import { RecPatch } from "~/dataProvider/tools"
import { EncryptionRuleResponseDto, MaskingRule, ExtraIgnoreItem } from "~/gen/api/encrypt"
import { FieldMappingInput, FieldMappingResponseDto, EncryptMappingStatus, SecondaryClassificationResponseDto } from "~/gen/api/governance"
import { useMutateEncryptionMappingsContext } from "~/pages/obfuscation/mutateEncryptionMappingsContext"
import { FieldMappingCatagory } from "~/pages/obfuscation/mutateEncryptionMappingsController"
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";


export type SelectedEncryptor = '' | 'skip' | EncryptionRuleResponseDto

export const buildInitialValue = (field:FieldMappingResponseDto) => ({
  ...field,
  fieldId: field.fieldId,
  isSkipped: field.encryptMappingStatus === EncryptMappingStatus.Skipped,
  secondaryClassificationId: field.secondaryClassificationId,
  maskingRule: 
    field.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour ? MaskingRule.ReplaceWithCharacter 
    : field.maskingRule === MaskingRule.None ? MaskingRule.None
    : (field.maskingRule || ''),
  ...({
    preserveLast4: field.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour,
  } as any),
  maskingValue: field.maskingValue || '',
})

export const useModifiers = (
  classifications: SimpleOption[],
  formikCtx: FormikContextType<Record<Identifier, FieldMappingInput>>,
  builtInMappingRulesBag: RecordMap<EncryptionRuleResponseDto & RecPatch> | undefined,
  listCtx: ListControllerProps<FieldMappingResponseDto & RecPatch>,
) => {
  const { resetForm, touched, values, setFormikState, validateForm } = formikCtx
  
  const synthClassificationValue = (fieldId:Identifier) => {
    const sndClsId = values[fieldId]?.secondaryClassificationId || -1
    return (classifications.find(c => c.key === sndClsId) as any) as SimpleOption || null
  }
  
  const synthEncryptRuleValue: (x?: FieldMappingResponseDto) => SelectedEncryptor = (x) => {
    if (x?.fieldId) {
      const editValue = values[x.fieldId]
      if (editValue) {
        return editValue.isSkipped ? 'skip'
        : ((builtInMappingRulesBag && builtInMappingRulesBag[editValue.encryptionRuleId || '']) 
          || '')
      }
    }
    return x?.isSkipped ? 'skip'
    : ((builtInMappingRulesBag && builtInMappingRulesBag[x?.encryptionRuleId || '']) 
      || '')
  }
  
  const { selectedIds, onToggleItem, ids: fieldIds, data: fieldsBag, onUnselectItems } = listCtx

  const acceptFormChange = (fieldId: number, patchVal:any, patchTouched:any) => {
    setFormikState((state) => {
      const newState = {
        ...state,
        values: {
          ...state.values,
          [fieldId]: {
            ...(state.values[fieldId]),
            ...patchVal,
          }
        },
        touched: {
          ...state.touched,
          [fieldId]: (
            patchTouched != null 
            ? {
              ...(state.touched[fieldId]),
              ...patchTouched,
            }
            // if patchTouched == null, must have set classification to '[None]',
            : {}),
        }
      }
      setTimeout(() => validateForm(newState.values))
      return newState
    })
  }

  const handleEncryptorChange = (x?: FieldMappingResponseDto) => (e: SelectChangeEvent<SelectedEncryptor>) => {
    if (!x || x.fieldId === undefined) { return }
    const fieldId = x.fieldId

    let toCata:FieldMappingCatagory  = 'skip'
    let patchVal: any = {}, patchTouched: any = {}
    if (e.target.value === 'skip') {
      toCata = 'skip'
      patchVal = {
        encryptionRuleId: null,
        isSkipped: true
      }
      patchTouched = {
        encryptionRuleId: x.encryptMappingStatus !== EncryptMappingStatus.Skipped,
      }
    } else {
      toCata = 'mapped'
      const encryptor = e.target.value as EncryptionRuleResponseDto
      patchVal = {
        encryptionRuleId: encryptor.id,
        isSkipped: false
      }
      patchTouched = {
        encryptionRuleId: 
          x.encryptMappingStatus !== EncryptMappingStatus.Mapped
          || encryptor.id !== x.encryptionRuleId,
      }
    }
    acceptFormChange(fieldId, patchVal, patchTouched)

    if (toCata !== 'skip' && selectedIds.some(id => id === x.fieldId)) {
      onToggleItem(x.fieldId)
    }
  }
  
  const valuesRef = useRef(values)
  valuesRef.current = values
  const isRowSelectable = useCallback((r: FieldMappingResponseDto & RecPatch, isSelectAllContext?: boolean) => {
    if (!isSelectAllContext) return true
    const editVal = valuesRef.current[r.id]
    return r.encryptMappingStatus === EncryptMappingStatus.PendingReview && 
      determineMappingCatagory(editVal) === 'pending-review'
  }, [])


  const unselectAllFieldSkips = useUnselectAll('fields')
  const { setMappingAmend, updateEditsCount } = useMutateEncryptionMappingsContext()

  const discardAllEdits = () => {
    resetForm()
    setMappingAmend({...initialAmend})
    unselectAllFieldSkips()
  }

  const listCtxRef = useRef(listCtx)
  listCtxRef.current = listCtx
  const bulkEditMappings = useCallback(() => {
    const {selectedIds, data: fieldsBag} = listCtxRef.current
    if (!selectedIds || !fieldsBag) return

    setFormikState((state) => {
      const patchVal:any = {}, patchTouched:any = {}
      for(var id of selectedIds) {
        const field = fieldsBag[id]
        const preVal = state.values[id]
        const preTouched = state.touched[id]
  
        patchVal[id] = {
          ...preVal,
          secondaryClassificationId: 100, // Public-Default
          isSkipped: true,  
          encryptionRuleId: null,  
          maskingRule: MaskingRule.None,  
          maskingValue: '',
          preserveLast4: false,
        }
        patchTouched[id] = {
          ...preTouched,
          secondaryClassificationId: field.secondaryClassificationId !== 100,
          encryptionRuleId: !!field.encryptionRuleId || field.encryptMappingStatus !== EncryptMappingStatus.Skipped,
          maskingRule: field.maskingRule !== MaskingRule.None,
          maskingValue: field.maskingValue !== '',
        }
      }
      const newState = {
        ...state,
        values: {
          ...state.values,
          ...patchVal,
        },
        touched: {
          ...state.touched,
          ...patchTouched,
        },
      }
      setTimeout(() => validateForm(newState.values))
      return newState
    })
    onUnselectItems()
  }, [onUnselectItems, setFormikState, validateForm])

  const mappingEditsCount = listCtx.ids.reduce<number>((acc, id) => {
    const t = Object.values(formikCtx.touched[id] || {})
    return acc + (formikCtx.touched[id] && (t.length === 0 || t.some(v => !!v)) ? 1 : 0)
  }
  , 0)
  updateEditsCount(mappingEditsCount)

  useEffect(() => {
    setMappingAmend(
      fieldIds.reduce((acc, id) => {
        if (touched[id] && touched[id]?.encryptionRuleId) {
          const field = fieldsBag[id]
          const editVal = values[id]
          const fromCata = 
            field.encryptMappingStatus === EncryptMappingStatus.PendingReview ? 'pending-review'
            : field.encryptMappingStatus === EncryptMappingStatus.Skipped ? 'skip'
            : 'mapped'
          const toCata = 
            editVal.isSkipped ? 'skip'
            : editVal.encryptionRuleId ? 'mapped'
            : 'pending-review'
          acc[fromCata] = acc[fromCata] - 1
          acc[toCata] = acc[toCata] + 1
        }
        return acc
      }, { ...initialAmend })
    )
  }, [formikCtx.touched, mappingEditsCount])

  return {
    synthClassificationValue,
    synthEncryptRuleValue,
    acceptFormChange,
    handleEncryptorChange,
    isRowSelectable,
    discardAllEdits,
    bulkEditMappings,
    mappingEditsCount,
  }
}

const determineMappingCatagory = (x: { 
  isSkipped?: boolean | null, 
  encryptionRuleId?: number | null, 
  customEncryptionRuleId?: number | null 
}): FieldMappingCatagory => {
  if (x.isSkipped) {
    return 'skip'
  } else if (Number(x.encryptionRuleId) > 0 || Number(x.customEncryptionRuleId) > 0) {
    return 'mapped'
  } else {
    return 'pending-review'
  }
}

const initialAmend = {
  mapped: 0,
  skip: 0,
  'pending-review': 0,
}


export const validatorBuilder = (
  refLookup: { 
    formikCtrl: FormikContextType<Record<Identifier, FieldMappingInput>>, 
  }, 
  classifications: SecondaryClassificationResponseDto[]
) => (values: Record<Identifier, FieldMappingInput>) => {
  return new Promise((resolve) => { // wait timeout for touched state to stabilise
    setTimeout(() => {
      const ids = Object.keys(refLookup.formikCtrl?.touched || {})
      
      const errors = ids.reduce((acc, id) => {
        if (refLookup.formikCtrl?.touched && Object.keys(refLookup.formikCtrl.touched[id] as any).length === 0)
        {
          // when classification choosed '[None]', we should skip validation
          return acc;
        }
        const input = values[id]
        const field = input as FieldMappingResponseDto
        const skipMappingValidate = !!(field.isIdentity || field.isComputed || field.extraIgnoreItem !== ExtraIgnoreItem.None)
        
        const errorBag = {} as Record<string, string>
        
        //@ts-ignore
        if (input.maskingRule === '') {
          if (!skipMappingValidate) {
            errorBag['maskingRule'] = 'Masking Rule is required'
          }
    
        } else if (input.maskingRule !== MaskingRule.None) {
          const maskingKind = decideDataTypeMaskingKind(field.dataType || '')
          const maskingValue = input.maskingValue || ''
    
          if (input.maskingValue === '') {
            errorBag['maskingValue'] = 'Masking Value is required'
          } else if (maskingKind === 'date') {
            if (!/\d{4}-\d{2}-\d{2}$/.test(maskingValue.trim())
            || isNaN(Date.parse(maskingValue.trim()))
            ) {
              errorBag['maskingValue'] = 'Invalid date string'
            }
          } else if (maskingKind === 'integer_num') {
            const mv = maskingValue.trim()
            if (!/^-?\d+$/.test(mv)) {
              errorBag['maskingValue'] = 'Invalid masking value'
            } else if (!integerRangeCheck(mv, field.dataType || '')) {
              errorBag['maskingValue'] = 'Invalid masking value (numeric range)'
            }
          } else if (maskingKind === 'non_integer_num') {
            const mv = maskingValue.trim()
            const i = mv.indexOf('.')
            if (isNaN(Number.parseFloat(mv)) 
            || (i > -1 && !/^\d*$/.test(mv.substring(i + 1)))
            || (i === -1 && !/^-?\d+(.\d+)?$/.test(mv))
            ) {
              errorBag['maskingValue'] = 'Invalid masking value'
            } else if (!nonIntegerRangeCheck(mv, field.dataType || '', field.precision, field.scale)) {
              errorBag['maskingValue'] = 'Invalid masking value (numeric range or scale)'
            }
          } 
        }
    
        if (!input.secondaryClassificationId) {
          errorBag['secondaryClassificationId'] = 'Classification is required'
        }
        //@ts-ignore
        if (!input.isSkipped && !input.encryptionRuleId) {
          if (!skipMappingValidate) {
            errorBag['encryptionRuleId'] = 'Encryption Rule is required'
          }
        }
    
        if (Object.keys(errorBag).length > 0) {
          acc[id] = errorBag
        }
        return acc
      }, {} as Record<Identifier, Record<string, string>>)
      resolve(errors)
    }, 5);
  })
}


const fieldsSorter = (a:FieldDto, b:FieldDto) => a.fieldId! - b.fieldId!

interface FieldDto {
  fieldId: number;
  fieldName: string;
}

export const useBigTableMode = ({isFieldMapped, bulkEditMappings, isRowSelectable, clearFieldMapping}: {
  isFieldMapped: (field: FieldDto) => boolean,
  bulkEditMappings: () => void,
  isRowSelectable: (r: FieldDto & RecPatch, isSelectAllContext?: boolean) => boolean,
  clearFieldMapping: (field: FieldDto) => void,
}) => {
  const listCtx = useListContext<FieldDto & RecPatch>()
  const { ids, data, onSelect } = listCtx

  const bigTableModeOn = ids.length > 60
  
  const groupedFields = useMemo(() => {
    let groupedFields = { charted: [] as FieldDto[], uncharted: [] as FieldDto[], unchartedIds: new Set<number>()}
    if (bigTableModeOn) {
      groupedFields = ids.reduce((acc, id) => {
        const x : FieldDto = data[id]
        if (isFieldMapped(x)) {
          acc.charted.push(x)
        } else {
          acc.uncharted.push(x)
        }
        return acc
      }, groupedFields)
    }
    groupedFields.charted.sort(fieldsSorter)
    groupedFields.uncharted.sort(fieldsSorter)
    groupedFields.unchartedIds = new Set(groupedFields.uncharted.map(x => x.fieldId!))
    return groupedFields
  }, [bigTableModeOn, ids, data])
  
  const [states, setStates] = useState({
    popoutOpen: false,
    staged: {
      ids: [] as number[],
      data: {} as Record<number, FieldDto>
    },
  })

  const resetStagedFields = useCallback(() => setStates(v => ({...v, staged: { ids: [], data: {}}})), [])
  useEffect(() => {
    resetStagedFields()
  }, [ids, resetStagedFields])

  const addFieldToEditor = (x:FieldDto) => {
    if (!groupedFields.unchartedIds.has(x.fieldId!) 
    || !!states.staged.data[x.fieldId!]) { return; }

    setStates(v => {
      const data = {...v.staged.data, [x.fieldId!]: x}
      return ({ ...v,
        staged: {
          ids: Object.values(data).sort(fieldsSorter).map(s => s.fieldId!),
          data,
        }
      })
    })
  }
  const removeFieldFromEditor = (id:number) => {
    if (!states.staged.data[id]) { return }
    const field = states.staged.data[id]
    setStates(v => {
      const data = v.staged.data
      delete data[id]
      return { ...v,
        staged: { 
          ids: Object.values(data).sort(fieldsSorter).map(s => s.fieldId!),
          data,
        }
      }
    })
    clearFieldMapping(field)
  }

  // when not in bigTableMode, useAutocomplete hook still expects an input element as ref,
  // we give it an input element not being used so the hook won't fail
  const nonActionInputRef = useRef({ 
    input: document.createElement('input'),
    getInputProps: () => ({ ref:{current:null}}) as any,
  })
  useEffect(() => {
    if (!bigTableModeOn) {
      const {input, getInputProps} = nonActionInputRef.current
      const inputRef : MutableRefObject<any> = (getInputProps() as any).ref
      inputRef.current = input
    }
  }, [bigTableModeOn])
  useEffect(() => {
    return () => {
      nonActionInputRef.current.input = null as any
      nonActionInputRef.current.getInputProps = null as any
      nonActionInputRef.current = null as any
    }
  }, [])

  const clearRef = useRef((x:any)=>{})

  const getOptionLabel = (x:FieldDto) => x.fieldName!
  const {
    getRootProps,
    getInputLabelProps,
    setAnchorEl,
    getClearProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    id,
    groupedOptions,
    inputValue,
  } = useAutocomplete({ 
    open: states.popoutOpen,
    clearOnBlur: false,
    openOnFocus: true,
    options: groupedFields.uncharted,
    getOptionLabel,
    onChange: (event, newValue, reason, details) => {
      if (reason === 'selectOption' && newValue) {
        addFieldToEditor(newValue)
        setTimeout(() => clearRef.current({}), 5)
      }
    },
    onOpen: () => setStates(v => ({...v, popoutOpen: true })),
    onClose: (event, reason) => {
      if (reason !== 'blur' && reason !== 'toggleInput') { 
        setStates(v => ({...v, popoutOpen: false })) 
      }
    }
  })

  clearRef.current = getClearProps().onClick!
  nonActionInputRef.current.getInputProps = getInputProps
  // check again just in case
  useEffect(() => {
    if (!bigTableModeOn) {
      const {input, getInputProps} = nonActionInputRef.current
      const inputRef : MutableRefObject<any> = (getInputProps() as any).ref
      inputRef.current = input
    }
  }, [bigTableModeOn])

  const isStagedField = (id:number) => Boolean(states.staged.data[id])
  const skipAllSelected = () => {
    const filteredFields = groupedOptions as FieldDto[]
    const toSelectIds = filteredFields.filter(x => isRowSelectable(x as any, true)).map(x => x.fieldId!)
    if (toSelectIds.length === 0) { return }
    onSelect(toSelectIds)
    setTimeout(() => {
      bulkEditMappings()
    })
  }

  const editorOnlyListCtx: ListControllerProps<FieldDto & RecPatch> = useMemo(() => {
    const newIds = [
      ...states.staged.ids, 
      ...(groupedFields.charted.map(x => x.fieldId!))
    ]
    const newData = {
      ...states.staged.data, 
      ...(groupedFields.charted.reduce((acc, x) => { 
        acc[x.fieldId!] = x; 
        return acc
      }, {} as any))
    }
    return { ...listCtx, ids: newIds, data: newData, } as any
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedFields.charted, states.staged])

  
  const overwriteRootProp = (props: any) => {
    const { onKeyDown: originKD, ...rest } = props
    const onKeyDown = (event: KeyboardEvent) => {
      const o = { key: '', preventDefault: () => {}}
      switch (event.key) {
        case 'PageUp': { 
          o.key = 'PageUp'  
          for(let i = 0; i < 2; i++) { originKD(o) }
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'PageDown': { 
          o.key = 'PageDown'  
          for(let i = 0; i < 2; i++) { originKD(o) }
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowUp': { 
          o.key = 'ArrowUp'  
          originKD(o); originKD(o);
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowDown': { 
          o.key = 'ArrowDown'  
          originKD(o); originKD(o);
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowLeft': { 
          o.key = 'ArrowUp'
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowRight': { 
          o.key = 'ArrowDown'
          event.preventDefault()
          originKD(o)
          break; 
        }
        default: {
          originKD(event)
          break;
        }
      }
    }
    return {...rest, onKeyDown}
  }

  const SearchInput = !bigTableModeOn ? null :
    <Box {...overwriteRootProp(getRootProps())}>
      <MuiTextField 
        {...({
          id,
          placeholder: 'Search & add mapping...',
          size: 'small',
          InputLabelProps: getInputLabelProps(),
          InputProps: {
            ref: setAnchorEl,
            endAdornment: (
              <Box>
                <IconButton tabIndex={-1} sx={{ visibility: states.popoutOpen ? 'visible' : 'hidden' }}
                  onClick={(event:any) => {  
                    setStates(v => ({...v, popoutOpen:false}))
                    const clear = getClearProps().onClick!
                    clear(event)
                  }}
                  aria-label="Clear"
                  title="Clear"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            ),
          },
          inputProps: {
            ...getInputProps(),
          },
        })}
      />
    </Box>
  const ListBox = !bigTableModeOn ? null :
    <Box sx={{ position: 'relative' }}>
      <Box component="ul" {...getListboxProps()}
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          maxHeight: 440, overflowY: 'auto',
          mt: 0,
          bgcolor: theme => alpha(theme.palette.success.light, 0.05),
          '& > li': {
            listStyle: 'none',
            mr: 2,
            p: 0.25,
            '&.Mui-focused': { bgcolor: 'action.hover' },
            '&[aria-disabled="true"]': {opacity: 'action.disabledOpacity', pointerEvents: 'none'},
            '&.Mui-focusVisible': { bgcolor: 'action.focus' },
            '&[aria-selected="true"]': {
              bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
              '&.Mui-focused': { bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity) },
              '&.Mui-focusVisible': { bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity) },
            },
          }
        }}
      >
        {groupedOptions.map((option:any, index:number) => {
          const fieldname = getOptionLabel(option as any)
          const matches = match(fieldname, inputValue, {insideWords:true});
          const parts = parse(fieldname, matches);
          return (
            <li key={(option as any).fieldId} {...getOptionProps({option, index} as any)}>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400}}>
                  {part.text}
                </span>
              ))}
            </li>)
        })}
      </Box>
    </Box>
  return {
    bigTableModeOn,
    SearchInput,
    ListBox,
    popoutOpen: states.popoutOpen,
    filteredItemCount: groupedOptions.length,
    closePopout: () => setStates(v => ({...v, popoutOpen: false})),
    resetStagedFields,
    ...(bigTableModeOn 
      ? {
        addFieldToEditor,
        removeFieldFromEditor,
        isStagedField,
        skipAllSelected,
        editorOnlyListCtx,
      }
      : {
        addFieldToEditor: (x: FieldMappingResponseDto) => {},
        removeFieldFromEditor: (id: number) => {},
        isStagedField: (id: number) => false,
        skipAllSelected: () => {},
        editorOnlyListCtx: {} as any
      })
  }
}
